import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Typography,
  Button,
  Box,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { getIframeVisibleCenter } from '../utils/iframe'

interface RemoveConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  disabled?: boolean
}

export default function RemoveConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  disabled,
}: RemoveConfirmDialogProps) {
  const [center, setCenter] = useState(0)
  useEffect(() => {
    if (open) setCenter(getIframeVisibleCenter())
  }, [open])

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      BackdropProps={{
        sx: { bgcolor: 'rgba(0, 0, 0, 0.25)' },
      }}
      PaperProps={{
        sx: (theme) => ({
          borderRadius: 2,
          boxShadow: 24,
          border: '2px solid',
          borderColor: 'error.main',
          bgcolor: theme.palette.common.white,
          marginTop: `${center}px`,
          transform: 'translateY(-50%)',
        }),
      }}
    >
      <DialogContent
        sx={{ p: 0, overflow: 'visible', bgcolor: 'common.white' }}
      >
        <IconButton
          aria-label='Close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.600',
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack
          spacing={2}
          alignItems='center'
          sx={{ textAlign: 'center', pt: 4, pb: 3, px: 3 }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberRoundedIcon
              sx={{ fontSize: 32, color: 'common.white' }}
            />
          </Box>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Are you sure you want to remove the selected item(s)?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
        <Button
          variant='outlined'
          size='medium'
          onClick={onClose}
          disabled={disabled}
          sx={{
            borderColor: 'error.main',
            color: 'error.main',
            minWidth: 100,
            py: 1.25,
          }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          size='medium'
          onClick={handleConfirm}
          disabled={disabled}
          sx={{ minWidth: 140, py: 1.25 }}
        >
          Yes, Remove
        </Button>
      </DialogActions>
    </Dialog>
  )
}

RemoveConfirmDialog.defaultProps = {
  disabled: false,
}
